import React from "react";
import { Clock, Mail, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const BOXHERO_LOGO_COLOR =
  "https://customer-assets.emergentagent.com/job_client-hub-266/artifacts/ysfg7n2y_Asset%209blue.png";

export default function PendingApproval() {
  const { user, account, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(false);

  const handleSignOut = async () => {
    await logout();
    navigate("/login");
  };

  const handleRefresh = async () => {
    setChecking(true);
    await refresh();
    setChecking(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div data-testid="pending-approval-page" className="w-full max-w-lg bg-white border border-slate-200 rounded-xl p-10 shadow-sm text-center">
        <div className="flex items-center justify-center mb-8">
          <img src={BOXHERO_LOGO_COLOR} alt="BoxHero" className="h-10 w-auto" />
        </div>

        <div className="w-16 h-16 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mx-auto">
          <Clock className="w-7 h-7" />
        </div>

        <h1 className="text-2xl font-bold text-slate-900 mt-5" style={{ fontFamily: "Manrope" }}>
          Account pending approval
        </h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Thanks {user?.name?.split(" ")[0] || "for signing up"}. Your account is being reviewed by the BoxHero team.
          You'll get access to your dashboard once an admin verifies your details and links you to the right billing and shipping accounts.
        </p>

        <div className="mt-6 text-left bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-2">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          {account?.company_name && <Row label="Company" value={account.company_name} />}
          <Row label="Status" value={<span className="inline-flex items-center gap-1.5 text-orange-700 font-semibold"><Clock className="w-3.5 h-3.5" /> Pending</span>} />
        </div>

        <p className="text-xs text-slate-500 mt-6 flex items-center justify-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          We'll email you the moment your account is approved.
        </p>

        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            data-testid="pending-refresh-btn"
            onClick={handleRefresh}
            disabled={checking}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors"
          >
            {checking ? "Checking…" : "I've been approved — refresh"}
          </button>
          <button
            data-testid="pending-signout-btn"
            onClick={handleSignOut}
            className="text-sm text-slate-600 hover:text-slate-900 px-4 py-2.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}
